import React, { useEffect } from 'react';
import useForm, { FormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Typography,
    Button,
    Container,
    Paper
} from '@material-ui/core';
import { addThread, getThread, editThread } from '../../actions/threadActions';

import { resizeFile } from '../../utils/imageManager';

import TextField from '../form/TextField';
import Spinner from '../common/Spinner';
import MainImageSelector from '../form/MainImageSelector';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
    },
    container: {
        maxWidth: '80rem',
        margin: 'auto',
        padding: '5rem 1rem',
    },
    heading: {
        textAlign: 'center',
        color: theme.palette.primary.main,
        marginBottom: '4rem',
        fontSize: '2.4rem',
        fontWeight: 'bold',
    },
    form: {
        margin: 0,
    },
    field: {
        marginBottom: '1.5rem',
    },
    label: {
        color: theme.palette.primary.dark,
        fontSize: '1.8rem',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        display: 'block',
    },
    successText: {
        color: theme.palette.success.main,
        fontSize: '1.4rem',
        marginBottom: '1.5rem',
    },
    buttonContainer: {
        textAlign: 'center',
        margin: '2rem 0',
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '1.6rem',
        padding: '1rem 2rem',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[400],
            cursor: 'not-allowed',
        },
    },
}));

const ThreadEditForm = (props) => {
    const classes = useStyles();
    const threadId = props.match.params.id;
    const dispatch = useDispatch();
    const loading = useSelector(state => state.common.loading);
    const selectedThread = useSelector(state => state.thread.selectedThread);
    const methods = useForm(); // initialise the hook
    const { register, setValue, handleSubmit } = methods;

    useEffect(() => {
        register({ name: 'mainImage' });

        if (threadId) {
            dispatch(getThread(threadId));
        }
    }, [register, threadId, dispatch])

    useEffect(() => {
        if (threadId && selectedThread) {
            setValue("title", selectedThread.title);
            setValue("details", selectedThread.details);
        }
    }, [threadId, selectedThread, setValue])

    const onSubmit = async data => {
        if (threadId) {
            dispatch(editThread(threadId, data));
        }
        else {
            let fd = new FormData();
            if (data.mainImage) {
                //Resize image here
                let resizedFile = await resizeFile(data.mainImage, 1024, data.mainImage.name);
                fd.append('photo', resizedFile, resizedFile.name);
            }
            fd.append('title', data.title);
            fd.append('details', data.details);
            if (data.email) {
                fd.append('email', data.email);
            }

            dispatch(addThread(fd));
        }
    }

    const handleFileChange = file => {
        setValue('mainImage', file);
    }

    return (
        <div className={classes.root}>
            <Container className={classes.container}>
                <Typography variant="h1" className={classes.heading}>
                    新規投稿
                </Typography>
                <FormContext {...methods} >
                    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
                        <Box className={classes.field}>
                            <TextField
                                label="タイトル"
                                type="title"
                                placeholder="タイトルを入力してください"
                                name="title"
                                registerOptions={{ required: true, maxLength: 100 }}
                            />
                        </Box>
                        <Box className={classes.field}>
                            <TextField
                                label="詳細"
                                type="textarea"
                                name="details"
                                rows={15}
                                registerOptions={{ required: true, minLength: 6, maxLength: 5000 }}
                            />
                        </Box>
                        <Box className={classes.field}>
                            <TextField
                                label="連絡先メールアドレス"
                                type="email"
                                placeholder="メールアドレスを入力してください"
                                name="email"
                                registerOptions={{ maxLength: 100 }}
                            />
                        </Box>
                        <Typography className={classes.successText}>
                            メールアドレスを設定すると他のユーザーがNZワークス経由で連絡する事が可能になります。
                        </Typography>
                        <Typography className={classes.successText}>
                            （メールアドレス自体が公開される事はありません）
                        </Typography>
                        {threadId ? null : (
                            <Box className={classes.field}>
                                <Typography className={classes.label}>メイン画像</Typography>
                                <MainImageSelector onFileChange={handleFileChange} />
                            </Box>
                        )}

                        <Box className={classes.buttonContainer}>
                            <Button
                                type="submit"
                                className={classes.submitButton}
                                disabled={loading}
                                variant="contained"
                            >
                                {threadId ? '更新' : '投稿する'}
                            </Button>
                        </Box>
                    </form>
                </FormContext>
                {loading ? <Spinner cover={true} /> : null}
            </Container>
        </div>
    )
}

export default ThreadEditForm
